import { yupResolver } from '@hookform/resolvers/yup';
import { Box, SvgIcon, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  AutocompleteOptions,
  Button,
  FullScreenLoader,
  SelectFieldWrapper,
} from '@Components/index';
import Modal from '@Components/Modal';
import Icons from '@Constants/Icons.constants';
import {
  useChangeDialerSubAccountMutation,
  useGetDialerSubAccountOptionsQuery,
} from '@Services/settings.service';
import { BaseAlertProps } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';

import {
  DialerSubAccountModalInput,
  DialerSubAccountModalProps,
} from './DialerSubAccountModal.types';

const DialerSubAccountModal: FunctionComponent<DialerSubAccountModalProps> = (
  props: DialerSubAccountModalProps
): JSX.Element => {
  const { isOpen, setIsOpen, userId, userName } = props;

  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [isSuccessfullyModified, setIsSuccessfullyModified] =
    useState<boolean>(false);
  const [newSubAccountName, setNewSubAccountName] = useState<string>('');

  // load subAccount settings
  const {
    data: dataOfDialerSubAccountOptions,
    isLoading: loadingDialerSubAccountOptions,
    isFetching: fetchingDialerSubAccountOptions,
    isError: isErrorForGetDialerSubAccountOptions,
    error: errorForGetDialerSubAccountOptions,
  } = useGetDialerSubAccountOptionsQuery(userId);

  // change subAccount value by user id
  const [changeDialerSubAccount, changeDialerSubAccountMethods] =
    useChangeDialerSubAccountMutation();

  const isLoading =
    loadingDialerSubAccountOptions || fetchingDialerSubAccountOptions;

  const subAccountError = t('common.formErrorMessages.sub_account_required');

  const schema = yup
    .object({
      dialerSubAccount: yup
        .object()
        .nullable()
        .required(subAccountError)
        .shape({
          label: yup.string().required(subAccountError),
          value: yup.string().required(subAccountError),
        }),
    })
    .required();

  const getSubAccountOptions = (): AutocompleteOptions[] => {
    if (
      dataOfDialerSubAccountOptions &&
      dataOfDialerSubAccountOptions.availableSubAccounts
    ) {
      return dataOfDialerSubAccountOptions.availableSubAccounts.map(
        (availableSubAccount) => ({
          label: availableSubAccount.subAccountFriendlyName,
          value: availableSubAccount.id,
        })
      );
    }
    return [
      {
        label: '',
        value: '',
      },
    ];
  };

  const options = getSubAccountOptions();

  /** FORM METHODS */
  const methods = useForm<DialerSubAccountModalInput>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    shouldUnregister: true,
  });

  useEffect(() => {
    if (dataOfDialerSubAccountOptions) {
      const subAccount = dataOfDialerSubAccountOptions.userSubAccount;
      const subAccountName = subAccount
        ? subAccount.subAccountFriendlyName
        : '';
      const subAccountId = subAccount ? subAccount.id : '';
      methods.reset({
        dialerSubAccount: {
          label: subAccountName,
          value: subAccountId,
        },
      });
    }
  }, [methods, dataOfDialerSubAccountOptions]);

  /** Form Submit Handler (Save Button Click) */
  const onSubmit: SubmitHandler<DialerSubAccountModalInput> = (formData) => {
    if (formData && formData.dialerSubAccount) {
      setNewSubAccountName(formData.dialerSubAccount.label);
      return changeDialerSubAccount({
        userId,
        dialerSubAccountId: formData.dialerSubAccount.value,
      });
    }
  };

  /**
   * Error and Success alerts for changing subAccount settings
   */
  useEffect(() => {
    if (changeDialerSubAccountMethods.error) {
      triggerAlert(changeDialerSubAccountMethods.error, setAlertState);
    }
    if (changeDialerSubAccountMethods.isSuccess) {
      setIsSuccessfullyModified(true);
    }
  }, [
    changeDialerSubAccountMethods.error,
    triggerAlert,
    changeDialerSubAccountMethods,
  ]);

  /**
   * Error Alerts for the Get subAccount settings
   */
  useEffect(() => {
    if (
      isErrorForGetDialerSubAccountOptions &&
      errorForGetDialerSubAccountOptions
    ) {
      triggerAlert(errorForGetDialerSubAccountOptions, setAlertState);
    }
  }, [
    triggerAlert,
    errorForGetDialerSubAccountOptions,
    isErrorForGetDialerSubAccountOptions,
  ]);

  const successfullyModifiedInfoSection = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      my={4}
    >
      <SvgIcon
        sx={{
          width: 33.3,
          height: 33.3,
          mb: 1.4,
          color: '#178549',
        }}
      >
        {Icons.checkCircle}
      </SvgIcon>
      <Typography
        fontSize="18px"
        fontWeight="600"
        marginX="30px"
        textAlign="center"
      >
        <Trans
          values={{
            userName,
            providerName: newSubAccountName,
          }}
          i18nKey="userCallerIds.Changed Dialer SubAccount"
          components={{ strong: <strong /> }}
        />
      </Typography>
    </Box>
  );

  return (
    <>
      <Modal
        open={isOpen}
        headerText={t('userCallerIds.Dialer SubAccount Settings')}
        onClose={() => {
          setIsOpen(false);
          setIsSuccessfullyModified(false);
        }}
        alertState={alertState}
      >
        <FormProvider {...methods}>
          {!isSuccessfullyModified && (
            <>
              {!isLoading ? (
                <Box
                  component="form"
                  role="form"
                  onSubmit={methods.handleSubmit(onSubmit)}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ padding: '3rem 2.4rem 2.4rem' }}
                  >
                    <Typography variant="main" mb={4} fontSize="16px">
                      <Trans
                        values={{ userName }}
                        i18nKey="userCallerIds.Dialer SubAccount Message"
                        components={{ strong: <strong /> }}
                      />
                    </Typography>
                    <SelectFieldWrapper
                      name="dialerSubAccount"
                      options={options}
                      multiple={false}
                      disableClearable
                      textFieldProps={{
                        label: `${t('userCallerIds.Dialer SubAccount')}`,
                        placeholder: t('userCallerIds.Dialer SubAccount'),
                      }}
                      sx={{ minWidth: '24.8rem', maxWidth: '24.8rem' }}
                    />
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                      borderTop: '0.1rem solid #A3ABB2',
                      padding: '3rem 2.4rem 2.4rem',
                    }}
                  >
                    <Box display="flex" justifyContent="flex-end">
                      <Button type="submit" color="primary">
                        {t('common.messages.Submit')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <FullScreenLoader />
              )}
            </>
          )}
          {isSuccessfullyModified && (
            <>
              {!isLoading ? (
                successfullyModifiedInfoSection()
              ) : (
                <FullScreenLoader />
              )}
            </>
          )}
        </FormProvider>
      </Modal>
    </>
  );
};

export default DialerSubAccountModal;
